import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * Android hardware/gesture Back routes through the same action as the
 * screen's on-screen back arrow. Header components (FormHeader, Header)
 * register their back handler while mounted; AppNavigator's BackHandler
 * listener runs the most recently registered one. Open RN Modals handle
 * Back themselves via onRequestClose before this ever fires.
 */
type BackAction = () => void;

const stack: { current: BackAction }[] = [];

/** The back action of the innermost mounted header, if any. */
export const getRegisteredBackAction = (): BackAction | undefined => stack[stack.length - 1]?.current;

/** Registers `onBack` (when defined) as the hardware Back action while mounted. */
export const useHardwareBackAction = (onBack: BackAction | undefined) => {
  const latest = useRef(onBack);
  latest.current = onBack;
  const enabled = Platform.OS !== 'web' && !!onBack;

  useEffect(() => {
    if (!enabled) return;
    const entry = { get current() { return latest.current as BackAction; } };
    stack.push(entry);
    return () => {
      const i = stack.lastIndexOf(entry);
      if (i >= 0) stack.splice(i, 1);
    };
  }, [enabled]);
};
